import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "__chunk_reload_attempt__";

const isChunkError = (err: unknown) => {
  const msg = (err as Error)?.message || String(err);
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk [\d]+ failed|error loading dynamically imported module/i.test(
    msg,
  );
};

/**
 * lazy() wrapper that recovers from stale chunks after a new deploy.
 * - Retries the import once after a short delay (handles transient CDN misses).
 * - If still failing, force-reloads the page once per minute so the browser
 *   fetches the fresh index.html and its updated asset hashes.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkError(err)) throw err;
      // Second attempt — transient network/CDN propagation.
      try {
        await new Promise((r) => setTimeout(r, 400));
        return await factory();
      } catch (err2) {
        if (!isChunkError(err2)) throw err2;
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        if (Date.now() - last > 60_000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
        }
        throw err2;
      }
    }
  });
}
